import {Num, Obj, Str} from '@repo/shared/api/core/api_schema';
import type {SchemaToType} from '@repo/shared/api/core/api_schema';

export const AVAILABILITY_VERSION = 1;
export const MAX_AVAILABILITY_BYTES = 16 * 1024 * 1024;
export const AvailabilityManifestSchema = Obj({
  schemaVersion: Num(),
  hash: Str(),
  bytes: Num(),
  compressedBytes: Num(),
  count: Num(),
});
export type AvailabilityManifest = SchemaToType<typeof AvailabilityManifestSchema>;
export interface AvailabilityFile {
  schemaVersion: number;
  // A missing IMDb ID means no healthy cached release in this complete snapshot.
  movies: Record<string, number>;
}
export const AVAILABILITY = {
  healthy: 1,
  audioEn: 2,
  audioEnSubEn: 4,
  audioEnSubFr: 8,
  audioFr: 16,
  audioFrSubEn: 32,
  audioFrSubFr: 64,
  subEn: 128,
  subFr: 256,
} as const;
export function availabilityObjectKey(manifest: Pick<AvailabilityManifest, 'hash'>): string {
  return `catalogue/v1/availability/${manifest.hash}.json`;
}
export function isAvailabilityMask(value: unknown): value is number {
  if (!Number.isSafeInteger(value) || typeof value !== 'number' || value < 0 || value > 511)
    return false;
  if (value && !(value & AVAILABILITY.healthy)) return false;
  return [
    [AVAILABILITY.audioEnSubEn, AVAILABILITY.audioEn | AVAILABILITY.subEn],
    [AVAILABILITY.audioEnSubFr, AVAILABILITY.audioEn | AVAILABILITY.subFr],
    [AVAILABILITY.audioFrSubEn, AVAILABILITY.audioFr | AVAILABILITY.subEn],
    [AVAILABILITY.audioFrSubFr, AVAILABILITY.audioFr | AVAILABILITY.subFr],
  ].every(([pair, required]) => !(value & pair!) || (value & required!) === required);
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
export function isAvailabilityManifest(value: unknown): value is AvailabilityManifest {
  return (
    object(value) &&
    value['schemaVersion'] === AVAILABILITY_VERSION &&
    typeof value['hash'] === 'string' &&
    /^[a-f0-9]{64}$/u.test(value['hash']) &&
    ['bytes', 'compressedBytes', 'count'].every(
      key => Number.isSafeInteger(value[key]) && Number(value[key]) >= (key === 'count' ? 0 : 1),
    ) &&
    Number(value['bytes']) <= MAX_AVAILABILITY_BYTES &&
    Number(value['compressedBytes']) <= MAX_AVAILABILITY_BYTES &&
    Number(value['count']) <= 300_000
  );
}
export function isAvailabilityFile(
  value: unknown,
  expectedCount?: number,
): value is AvailabilityFile {
  if (!object(value) || value['schemaVersion'] !== AVAILABILITY_VERSION || !object(value['movies']))
    return false;
  const entries = Object.entries(value['movies']);
  return (
    entries.length <= 300_000 &&
    (expectedCount === undefined || entries.length === expectedCount) &&
    entries.every(([id, mask]) => /^\d{7,10}$/u.test(id) && isAvailabilityMask(mask))
  );
}
